import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import dayjs from 'dayjs';

import MainCard from 'components/MainCard';
import PageHeader from 'components/PageHeader';
import CurrencyAmount from 'components/currency/CurrencyAmount';
import TableActionButton from 'components/table/TableActionButton';
import TableActions from 'components/table/TableActions';
import TableActionCell from 'components/table/TableActionCell';
import ClickableTableRow, { stopRowClickPropagation } from 'components/table/ClickableTableRow';
import { orderStatusChipSx, paymentStatusChipSx } from 'components/table/tableChipStyles';
import { useToast } from 'contexts/ToastContext';
import { listTransactions } from 'services/payment.service';
import { brand } from 'utils/designTokens';

import TransactionDetailsDialog from './TransactionDetailsDialog';
import RefundTransactionDialog from './RefundTransactionDialog';

const METHOD_OPTIONS = [
  { value: 'all', label: 'All Methods' },
  { value: 'stripe', label: 'Card / Stripe' },
  { value: 'cash', label: 'Cash On Delivery' }
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'paid', label: 'Paid / Succeeded' },
  { value: 'pending', label: 'Pending' },
  { value: 'partially_refunded', label: 'Partially Refunded' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'failed', label: 'Failed' }
];

export default function TransactionList() {
  const { showApiError } = useToast();

  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Dialog states
  const [detailsDialog, setDetailsDialog] = useState({ open: false, id: null });
  const [refundDialog, setRefundDialog] = useState({ open: false, item: null });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        payment_method: methodFilter,
        payment_status: statusFilter
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      const res = await listTransactions(params);
      const data = res?.data;
      const list = Array.isArray(data?.transactions) ? data.transactions : [];

      setTransactions(list);
      setTotal(Number(data?.pagination?.total ?? data?.total) || list.length);
    } catch (err) {
      showApiError(err, 'Failed to load transactions.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, methodFilter, statusFilter, debouncedSearch, showApiError]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openDetails = (id) => {
    setDetailsDialog({ open: true, id });
  };

  const openRefund = (item) => {
    setRefundDialog({ open: true, item });
  };

  return (
    <Box>
      <PageHeader
        title="Payment Transactions"
        subtitle="View payments, inspect Stripe charges, review ledger balances, and issue refunds."
        count={total}
        actions={
          <Tooltip title="Refresh transactions">
            <span>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={loadTransactions}
                disabled={loading}
                startIcon={<ReloadOutlined spin={loading} />}
                sx={{ borderRadius: 2 }}
              >
                Refresh
              </Button>
            </span>
          </Tooltip>
        }
      />

      <MainCard content={false}>
        {/* Filter Controls Bar */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${brand.border}` }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="tx-method-filter">Method</InputLabel>
                <Select
                  labelId="tx-method-filter"
                  value={methodFilter}
                  label="Method"
                  onChange={(e) => {
                    setMethodFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  {METHOD_OPTIONS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="tx-status-filter">Status</InputLabel>
                <Select
                  labelId="tx-status-filter"
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <TextField
              size="small"
              placeholder="Search by order #, customer, charge ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: { xs: '100%', md: 320 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined style={{ color: '#8c8c8c' }} />
                    </InputAdornment>
                  )
                }
              }}
            />
          </Stack>
        </Box>

        {/* Transactions Table */}
        <TableContainer sx={{ minHeight: 380, maxHeight: 620 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: brand.canvas, fontWeight: 700 } }}>
                <TableCell width={50}>#</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Payment Status</TableCell>
                <TableCell align="right">Grand Total</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Refunded</TableCell>
                <TableCell align="right">Refundable</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center" width={110}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Loading transactions...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                      No payment transactions found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((tx, idx) => {
                  const targetId = tx.transaction?.transaction_id || tx.order_id;
                  const isRefundable = Boolean(tx.financials?.is_refundable);

                  return (
                    <ClickableTableRow
                      key={targetId || idx}
                      onClick={() => openDetails(targetId)}
                    >
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {page * rowsPerPage + idx + 1}
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>
                          {tx.order_no || `#${tx.order_id}`}
                        </Typography>
                        {tx.order_status && (
                          <Chip
                            label={tx.order_status}
                            size="small"
                            sx={{ mt: 0.5, ...orderStatusChipSx(tx.order_status) }}
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {tx.customer?.name || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {tx.customer?.mobile_number || '—'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={tx.payment_method?.name || (tx.transaction?.gateway === 'stripe' ? 'Card / Stripe' : 'Cash')}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 22,
                            fontWeight: 600,
                            fontSize: '0.6875rem',
                            borderColor: 'rgba(31, 91, 64, 0.2)',
                            color: brand.primary
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        {tx.payment_status ? (
                          <Chip
                            label={tx.payment_status}
                            size="small"
                            sx={paymentStatusChipSx(tx.payment_status)}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <CurrencyAmount value={tx.financials?.grand_total} fontWeight={600} />
                      </TableCell>

                      <TableCell align="right">
                        <CurrencyAmount
                          value={tx.financials?.paid_amount}
                          fontWeight={700}
                          color="primary.main"
                        />
                      </TableCell>

                      <TableCell align="right">
                        <CurrencyAmount
                          value={tx.financials?.refunded_amount || 0}
                          fontWeight={600}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <CurrencyAmount
                          value={tx.financials?.remaining_refundable_amount || 0}
                          fontWeight={700}
                          color="#2e7d32"
                        />
                      </TableCell>

                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="caption">
                          {tx.created_at ? dayjs(tx.created_at).format('YYYY-MM-DD') : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {tx.created_at ? dayjs(tx.created_at).format('HH:mm') : ''}
                        </Typography>
                      </TableCell>

                      <TableActionCell>
                        <TableActions>
                          <TableActionButton
                            action="view"
                            title="View Transaction"
                            onClick={stopRowClickPropagation(() => openDetails(targetId))}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </TableActionButton>

                          {isRefundable && (
                            <Tooltip title="Issue Stripe Refund" arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={stopRowClickPropagation(() => openRefund(tx))}
                                  aria-label="Refund"
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1.5,
                                    border: '1px solid rgba(31, 91, 64, 0.2)',
                                    color: brand.primary,
                                    '&:hover': {
                                      bgcolor: 'rgba(31, 91, 64, 0.08)',
                                      borderColor: brand.primary
                                    }
                                  }}
                                >
                                  <CurrencyExchangeIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableActions>
                      </TableActionCell>
                    </ClickableTableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{ borderTop: `1px solid ${brand.border}` }}
        />
      </MainCard>

      {/* Dialogs */}
      <TransactionDetailsDialog
        open={detailsDialog.open}
        transactionId={detailsDialog.id}
        onClose={() => setDetailsDialog({ open: false, id: null })}
        onOpenRefund={(item) => openRefund(item)}
      />

      <RefundTransactionDialog
        open={refundDialog.open}
        item={refundDialog.item}
        onClose={() => setRefundDialog({ open: false, item: null })}
        onSuccess={loadTransactions}
      />
    </Box>
  );
}
